import React, { useState } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy } from '@fortawesome/pro-regular-svg-icons'
import { LabraToolTipV2, Truncate } from '@labrav/react-components'
import { useStyles } from './TextWithCopyIcon.styles'

interface TextWithCopyIconProps {
  text?: string
  displayText?: string
  truncateWidth?: string | number
}

const fallbackCopyToClipboard = (text: string): boolean => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  textarea.style.top = '-9999px'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.focus({ preventScroll: true })
  textarea.select()
  try {
    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    return success
  } catch {
    document.body.removeChild(textarea)
    return false
  }
}

export const TextWithCopyIcon: React.FC<TextWithCopyIconProps> = ({
  text,
  displayText,
  truncateWidth,
}) => {
  const [copied, setCopied] = useState(false)
  const { classes } = useStyles()

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!text) return

    let success = false
    try {
      await navigator.clipboard.writeText(text)
      success = true
    } catch {
      success = fallbackCopyToClipboard(text)
    }

    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } else {
      console.error('Failed to copy text to clipboard')
    }
  }

  if (!text || text === '-') {
    return <span>{text || '-'}</span>
  }

  const shown = displayText ?? text
  const showCompactLabel = displayText != null

  return (
    <div className={classes.root}>
      <div className={classes.textWrap}>
        {showCompactLabel ? (
          <span className={classes.displayText}>{shown}</span>
        ) : truncateWidth !== undefined ? (
          <Truncate width={truncateWidth}>{shown}</Truncate>
        ) : (
          <Truncate>{shown}</Truncate>
        )}
      </div>

      <LabraToolTipV2
        tooltipPlacement="top"
        description={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        <FontAwesomeIcon
          icon={faCopy}
          onClick={handleCopy}
          className={clsx(classes.icon, copied && classes.iconCopied)}
        />
      </LabraToolTipV2>
    </div>
  )
}
